import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import icCalendar from "@/assets/icons/ic-calendar.svg";
import { AX_NEWS_SECTIONS, type AxNewsItem, type AxNewsSectionKey } from "@/types/ax-news";
import {
  AXNewsService,
  type ICategory,
  type IGetListArticlesRes,
} from "@/services/ax-news-service";
import { formatDate } from "@/lib/utils";
import DOMPurify from "dompurify";
import { SafeImage } from "@/components/ui/safe-image/safe-image";
import icArrowLeft from "@/assets/icons/ic-arrow-left.svg";

const CATEGORY_TO_SECTION_KEY: Record<ICategory, AxNewsSectionKey> = {
  ai_use_case: "use-case",
  ax_trend: "trend",
  pwc_digital_solutions: "pwc-solutions",
};

const mapToAxNewsItem = (article: IGetListArticlesRes): AxNewsItem => ({
  id: article.id,
  section: CATEGORY_TO_SECTION_KEY[article.category] ?? "trend",
  image: article.cover_image_url,
  title: article.title,
  description: article.content,
  body: article.content,
  date: article.created_at,
  href: `/ax-news/list/${article.id}`,
});

export const AxNewsDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [item, setItem] = useState<AxNewsItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setIsLoading(true);
    AXNewsService.getArticleDetail(id)
      .then((res) => {
        if (!cancelled && res.data.data) setItem(mapToAxNewsItem(res.data.data));
      })
      .catch(() => {
        if (!cancelled) setItem(null);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (!item) {
    return null;
  }

  const sectionMeta = AX_NEWS_SECTIONS[item.section];

  return (
    <>
      {isLoading && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div
        className="flex flex-col gap-6 lg:gap-[48px] px-5 pt-6 pb-10 lg:px-[100px] lg:pt-10 lg:pb-[100px] max-w-[1440px] mx-auto w-full"
        style={{ display: isLoading ? "none" : "flex" }}
      >
        {/* Top section: back button + content (badges+title+image) */}
        <div className="flex flex-col gap-4 lg:gap-6">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="뒤로 가기"
            className="w-12 h-12 flex items-center justify-center"
          >
            <img src={icArrowLeft} alt="Icon Arrow Left" />
          </button>

          {/* Badges + title + image — 24px gap between all three */}
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center justify-center h-8 px-3 bg-[#EBEBEB] rounded-2xs text-caption font-medium text-[#000000] uppercase tracking-wide whitespace-nowrap">
                  {sectionMeta.badgeLabel}
                </span>
                <span className="inline-flex items-center gap-1 h-8 pl-2 pr-3 bg-[#EBEBEB] rounded-2xs text-[12px] font-medium text-[#000000] whitespace-nowrap">
                  <img src={icCalendar} alt="" width={20} height={20} className="shrink-0" />
                  {formatDate(item.date, "YYYY-MM-DD")}
                </span>
              </div>
              <h1 className="text-[32px] leading-[40px] lg:text-[32px] lg:leading-10 lg:tracking-[-0.96px] text-[#202020] font-medium">
                {item.title}
              </h1>
            </div>

            <div className="relative w-full h-[320px] rounded overflow-hidden bg-bg-fill-neutral-subtle">
              <SafeImage
                src={item.image}
                alt={item.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Article body — full-width mobile, 605px centered on desktop */}
        <article
          className="prose prose-neutral max-w-none w-full lg:max-w-[605px] lg:mx-auto text-body leading-6 tracking-[-0.16px] text-content-strong"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.body) }}
        />
      </div>
    </>
  );
};

export default AxNewsDetail;
